import { useApp } from '@/contexts';

import { useEffect, useState } from 'react';

import Editor from '@monaco-editor/react';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { createClient } from 'contentful';
import { useNavigate } from 'react-router';

import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import { logger } from '@/utils/logger';

import { useCreateRecipeMutation } from '../api/recipeEditorApi';

const accessToken = '0f243ce9285ce061d169b5d337e547755168c3972d67dc957df8362b504ccdfd';
const space = '0qhqhn3xauc3';
// Contentful configuration (same as legacy dataProtect newtransformer.js)
const contentfulClient = createClient({
  accessToken,
  space,
});

const RECIPE_CONTENT_TYPE_ID = 'recipe';

// Name validation regex (must contain letters or numbers)
const NAME_REGEX = /[A-Za-z0-9]/;

// Default template content (same as legacy)
const DEFAULT_TEMPLATE = `# This recipe converts some static JSON to an Excel file.
# To use this recipe, click Run!

# CONNECTORS specify what data will be pulled into the in-memory database during processing
connectors:
# Source data
-
    name: sourceLeadData
    type: json
    properties:
      jsonObject: >
       [
         { "leadId": "1", "firstName": "John", "lastName": "Thomas", "leadScore": 88 },
         { "leadId": "2", "firstName": "Anne", "lastName": "Jacobs", "leadScore": 32 }
       ]

# Target file
-
    name: excelFile
    type: localFileWriter
    format: excelFormat
    properties:
      fileName: "myFirstSpreadsheet.xlsx"

# FORMATS specify how files should be processed as they are being read or written to
formats:
-
    name: excelFormat
    type: excel
    properties:
      worksheets: "data"
      useHeader: "true"

# STATEMENTS specify how the file should be processed while in memory
statements:
# NOTE: You can add trasformations into your
  - statement: |
      (transformedData) => select
              leadId           lead_id,
              ucase(firstName) first_name,
              lastName         last_name,
              leadScore        lead_score
            from sourceLeadData
  - statement: print transformedData

# count the data
  - statement: (dataCount) => select count(*) from transformedData
  - statement: print dataCount

# insert the data
  - statement: INSERT transformedData INTO excelFile

# Add more statements to convert, join, aggregrate, transform, and integrate your data`;

const BLANK_TEMPLATE = '';

interface ContentfulTemplate {
  title: string;
  template: string;
  description?: string;
}

/**
 * NewRecipePage - Full-page recipe creation with template selection.
 * Matches legacy dataProtect newtransformer.js UI.
 *
 * Layout:
 * - Left: Recipe Name, Cancel/Create, Template list with search
 * - Right: Description, YAML Preview
 */
const NewRecipePage = () => {
  const { appId } = useApp();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [createRecipe, { isLoading: isCreatingRecipe }] = useCreateRecipeMutation();

  // Get connectorType from query params
  const searchParams = new URLSearchParams(window.location.search);
  const connectorTypeFilter = searchParams.get('connectorType')?.toLowerCase();

  // Form state
  const [name, setName] = useState('');
  const [nameChanged, setNameChanged] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('Default');

  // Template data from Contentful
  const [contentfulTemplates, setContentfulTemplates] = useState<ContentfulTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);

  // Fetch templates from Contentful on mount
  useEffect(() => {
    setIsLoadingTemplates(true);
    contentfulClient
      .getEntries({
        include: 3,
        content_type: RECIPE_CONTENT_TYPE_ID,
        order: ['fields.title'],
      })
      .then((entries) => {
        const templates: ContentfulTemplate[] = entries.items.map((item) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Contentful field types are dynamic
          const fields = item.fields as any;
          let templateContent = fields.RecipeContent || '';
          // Strip markdown code fences (same as legacy)
          templateContent = templateContent.replace('```\n', '').replace('\n```', '');
          return {
            title: fields.title || '',
            template: templateContent,
            description: fields.description || '',
          };
        });
        setContentfulTemplates(templates);
        setIsLoadingTemplates(false);
      })
      .catch((error) => {
        logger.error('Failed to fetch templates from Contentful:', error);
        setIsLoadingTemplates(false);
      });
  }, []);

  // Get the preview content based on selected template
  const getPreviewContent = () => {
    if (selectedTemplate === 'Default') return DEFAULT_TEMPLATE;
    if (selectedTemplate === 'Blank') return BLANK_TEMPLATE;
    const found = contentfulTemplates.find((t) => t.title === selectedTemplate);
    return found?.template || DEFAULT_TEMPLATE;
  };

  // Get the description for the current template
  const getCurrentDescription = () => {
    if (selectedTemplate === 'Default' || selectedTemplate === 'Blank') return '';
    const found = contentfulTemplates.find((t) => t.title === selectedTemplate);
    return found?.description || '';
  };

  // Handle template selection
  const handleTemplateChange = (templateName: string) => {
    setSelectedTemplate(templateName);
    if (!nameChanged) {
      setName(templateName === 'Default' || templateName === 'Blank' ? '' : templateName);
    }
  };

  // Handle name change
  const handleNameChange = (value: string) => {
    if (!nameChanged) setNameChanged(true);
    setName(value);
  };

  // Filter templates by search term AND connectorType
  const filteredTemplates = contentfulTemplates.filter((t) => {
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        t.title.toLowerCase().includes(term) || t.template.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }

    // Filter by connectorType if provided
    if (connectorTypeFilter) {
      const templateLower = t.template.toLowerCase();
      const titleLower = t.title.toLowerCase();
      return (
        templateLower.includes(connectorTypeFilter) || titleLower.includes(connectorTypeFilter)
      );
    }

    return true;
  });

  // Count total visible templates (Default + Blank + filtered contentful)
  const totalTemplateCount = 2 + filteredTemplates.length;

  // Handle create
  const handleCreate = async () => {
    if (!appId || !NAME_REGEX.test(name)) {
      if (!NAME_REGEX.test(name)) {
        showSnackbar('Recipe name must include letters or numbers', 'warning');
      }
      return;
    }

    const config = getPreviewContent();

    try {
      const result = await createRecipe({
        appId,
        name: name.trim(),
        config,
        description: '',
        languageVersion: '2.0',
        processingType: 'batch',
      }).unwrap();

      if (result.baseId) {
        showSnackbar('Recipe created successfully', 'success');
        navigate(`/a/${appId}/tf/${result.baseId}`);
      }
    } catch (error) {
      logger.error('Failed to create recipe:', error);
      showSnackbar('Failed to create recipe', 'error');
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(`/a/${appId}/tf`);
  };

  const previewContent = getPreviewContent();
  const description = getCurrentDescription();

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        pt: 1.5,
        px: 2,
      }}
    >
      {/* Left Column: Recipe Name, Buttons, Template List */}
      <Box
        sx={{
          width: 300,
          minWidth: 300,
          display: 'flex',
          flexDirection: 'column',
          mr: 0,
          mb: 2,
        }}
      >
        {/* Recipe Name */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, fontSize: '12px' }}>
            Recipe Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            id="recipe-name-input"
            autoComplete="off"
            inputProps={{ maxLength: 50 }}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreate();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '13px',
              },
            }}
          />
        </Box>

        {/* Cancel / Create Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'flex-start' }}>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={isCreatingRecipe}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={isCreatingRecipe || !NAME_REGEX.test(name)}
            onClick={handleCreate}
            sx={{ minWidth: 80 }}
          >
            {isCreatingRecipe ? <CircularProgress size={16} color="inherit" /> : 'Create'}
          </Button>
        </Box>

        {/* Template Header with count */}
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, fontSize: '12px' }}>
          Template ({totalTemplateCount})
          {connectorTypeFilter && (
            <Typography
              component="span"
              sx={{ ml: 1, fontSize: '11px', color: '#00853E', fontWeight: 'normal' }}
            >
              (filtered by {connectorTypeFilter})
            </Typography>
          )}
        </Typography>

        {/* Template Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search title or syntax"
          autoComplete="off"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            mb: 0,
            '& .MuiOutlinedInput-root': {
              fontSize: '13px',
              borderRadius: 0,
              '& input': {
                py: 1,
              },
            },
          }}
        />

        {/* Template List */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderTop: 'none',
          }}
        >
          {isLoadingTemplates ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List dense disablePadding>
              {/* (Default) */}
              <ListItemButton
                selected={selectedTemplate === 'Default'}
                onClick={() => handleTemplateChange('Default')}
                sx={{
                  py: 0.5,
                  '&.Mui-selected': {
                    bgcolor: '#eee',
                    color: '#000',
                    '&:hover': { bgcolor: '#e0e0e0' },
                  },
                }}
              >
                <ListItemText primary="(Default)" primaryTypographyProps={{ fontSize: '13px' }} />
              </ListItemButton>

              {/* (Blank) */}
              <ListItemButton
                selected={selectedTemplate === 'Blank'}
                onClick={() => handleTemplateChange('Blank')}
                sx={{
                  py: 0.5,
                  '&.Mui-selected': {
                    bgcolor: '#eee',
                    color: '#000',
                    '&:hover': { bgcolor: '#e0e0e0' },
                  },
                }}
              >
                <ListItemText primary="(Blank)" primaryTypographyProps={{ fontSize: '13px' }} />
              </ListItemButton>

              {/* Contentful Templates */}
              {filteredTemplates.map((template) => (
                <ListItemButton
                  key={template.title}
                  selected={selectedTemplate === template.title}
                  onClick={() => handleTemplateChange(template.title)}
                  sx={{
                    py: 0.5,
                    '&.Mui-selected': {
                      bgcolor: '#eee',
                      color: '#000',
                      '&:hover': { bgcolor: '#e0e0e0' },
                    },
                  }}
                >
                  <ListItemText
                    primary={template.title}
                    primaryTypographyProps={{ fontSize: '13px' }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Right Column: Description + Preview */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ml: 0,
          pt: 0,
        }}
      >
        {/* Description */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '12px' }}>
            Description
          </Typography>
          <Box
            sx={{
              minHeight: 50,
              maxHeight: 60,
              overflow: 'auto',
              mt: 0.5,
              mb: 1,
              px: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
              {description || 'No Description'}
            </Typography>
          </Box>
        </Box>

        {/* Preview */}
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, fontSize: '12px' }}>
          Preview
        </Typography>

        {/* Template Name Banner */}
        <Box
          sx={{
            py: 0.5,
            px: 1,
            bgcolor: '#666',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: '#eee', fontWeight: 'bold', fontSize: '13px' }}>
            {selectedTemplate} Template
          </Typography>
        </Box>

        {/* YAML Preview (read-only Monaco) */}
        <Box
          sx={{
            flex: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderTop: 'none',
            overflow: 'hidden',
          }}
        >
          <Editor
            value={previewContent}
            language="yaml"
            theme="vs-dark"
            options={{
              readOnly: true,
              minimap: { enabled: false },
              lineNumbers: 'on',
              fontSize: 13,
              tabSize: 2,
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              folding: true,
              renderLineHighlight: 'none',
              overviewRulerBorder: false,
              hideCursorInOverviewRuler: true,
              contextmenu: false,
              domReadOnly: true,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default NewRecipePage;
